import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, MapPin, Ruler, DollarSign,
  Calendar, User, AlertTriangle, ChevronRight,
  Filter, X, SlidersHorizontal,
  FolderOpen
} from 'lucide-react';
import { getDossiers } from '../api/dossiers';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
dayjs.locale('fr');

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0',
  sandDark:  '#e8d8c0',
  terra:     '#c2521a',
  terraLt:   '#e0784a',
  terraDk:   '#8a3510',
  forest:    '#1a3d28',
  forestMid: '#244d35',
  forestLt:  '#2f6645',
  gold:      '#c8902a',
  goldLt:    '#e8b84a',
  ink:       '#0e1a10',
  mist:      '#6e8070',
  cream:     '#faf6ef',
};

/* ─── STATUS CONFIG ─── */
const STATUTS = [
  { value: '',                        label: 'Tous les statuts' },
  { value: 'SOUMIS',                  label: 'Soumis' },
  { value: 'VERIFICATION_CADASTRALE', label: 'Vérif. cadastrale' },
  { value: 'ENQUETE_TERRAIN',         label: 'Enquête terrain' },
  { value: 'VERIFICATION_TITRES',     label: 'Vérif. titres' },
  { value: 'RAPPORT_EXPERTISE',       label: 'Rapport' },
  { value: 'CLOTURE',                 label: 'Clôturé' },
];

const STATUS_STYLE = {
  SOUMIS:                  { bg: 'rgba(200,144,42,.1)',  color: '#8a6010', dot: C.gold },
  VERIFICATION_CADASTRALE: { bg: 'rgba(26,61,40,.1)',    color: C.forestMid, dot: C.forestLt },
  ENQUETE_TERRAIN:         { bg: 'rgba(26,61,40,.12)',   color: C.forest,    dot: C.forestMid },
  VERIFICATION_TITRES:     { bg: 'rgba(47,102,69,.12)',  color: C.forestLt,  dot: C.forestLt },
  RAPPORT_EXPERTISE:       { bg: 'rgba(194,82,26,.1)',   color: C.terraDk,   dot: C.terra },
  CLOTURE:                 { bg: 'rgba(110,128,112,.1)', color: C.mist,      dot: C.mist },
};

const RESULTAT_STYLE = {
  EN_ATTENTE: { bg: 'rgba(200,144,42,.1)',  color: '#8a6010',  label: 'En attente' },
  VALIDE:     { bg: 'rgba(47,102,69,.12)',  color: C.forestLt, label: 'Validé ✓'   },
  LITIGE:     { bg: 'rgba(194,82,26,.12)',  color: C.terraDk,  label: 'Litige'     },
  RISQUE:     { bg: 'rgba(194,82,26,.08)',  color: C.terra,    label: 'Risque ⚠'   },
};

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Plus récent' },
  { value: 'date_asc',  label: 'Plus ancien' },
  { value: 'ref_asc',   label: 'Référence A→Z' },
  { value: 'urgent',    label: 'Urgents en premier' },
];

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'DM Sans', sans-serif; }

/* ── PAGE ── */
.dl-page { min-height: 100vh; padding: 36px 40px 60px; }
@media (max-width: 768px) { .dl-page { padding: 20px 16px 48px; } }

/* ── HEADER ── */
.dl-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}
.dl-eyebrow {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${C.terra};
  margin-bottom: 6px;
}
.dl-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -.035em;
  color: ${C.ink};
}
.dl-title em { font-style: italic; color: ${C.terra}; }
.dl-count {
  display: inline-flex;
  align-items: center;
  margin-left: 12px;
  background: rgba(194,82,26,.1);
  color: ${C.terraDk};
  font-size: .8rem;
  font-weight: 700;
  font-family: 'DM Sans', sans-serif;
  padding: 3px 11px;
  border-radius: 999px;
  vertical-align: middle;
  letter-spacing: 0;
}
.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 13px 22px;
  border-radius: 13px;
  background: ${C.forest};
  color: ${C.sand};
  font: 600 .9rem 'DM Sans', sans-serif;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(26,61,40,.2);
  transition: transform .25s, box-shadow .25s, background .2s;
  text-decoration: none;
  white-space: nowrap;
  flex: none;
  overflow: hidden;
  position: relative;
}
.btn-new::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent);
  transform: translateX(-100%);
  transition: transform .5s;
}
.btn-new:hover::before { transform: translateX(100%); }
.btn-new:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(26,61,40,.28); }

/* ── TOOLBAR ── */
.dl-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 380px;
}
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%; transform: translateY(-50%);
  color: ${C.mist};
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 12px 40px 12px 42px;
  border: 1.5px solid rgba(26,61,40,.12);
  border-radius: 13px;
  background: white;
  font: 500 .92rem 'DM Sans', sans-serif;
  color: ${C.ink};
  outline: none;
  transition: border-color .2s, box-shadow .2s;
}
.search-input:focus {
  border-color: ${C.terra};
  box-shadow: 0 0 0 4px rgba(194,82,26,.1);
}
.search-input::placeholder { color: rgba(110,128,112,.45); }
.search-clear {
  position: absolute;
  right: 12px;
  top: 50%; transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${C.mist};
  display: flex; align-items: center;
  padding: 3px;
  border-radius: 4px;
  transition: color .2s;
}
.search-clear:hover { color: ${C.terra}; }

/* filter chips */
.filter-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  flex: 1;
}
.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 999px;
  border: 1.5px solid rgba(26,61,40,.1);
  background: white;
  font: 500 .82rem 'DM Sans', sans-serif;
  color: ${C.mist};
  cursor: pointer;
  transition: border-color .2s, background .2s, color .2s;
  white-space: nowrap;
}
.filter-chip:hover { border-color: rgba(26,61,40,.22); color: ${C.forest}; }
.filter-chip.active {
  background: ${C.forest};
  border-color: ${C.forest};
  color: ${C.sand};
}
.filter-chip .chip-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
}

/* sort select */
.sort-select {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1.5px solid rgba(26,61,40,.1);
  background: white;
  font: 500 .82rem 'DM Sans', sans-serif;
  color: ${C.mist};
  cursor: pointer;
  outline: none;
  transition: border-color .2s;
  appearance: none;
}
.sort-select:focus { border-color: ${C.terra}; }

/* view toggle */
.view-toggle {
  display: flex;
  gap: 4px;
  background: white;
  border: 1.5px solid rgba(26,61,40,.1);
  border-radius: 12px;
  padding: 4px;
}
.view-btn {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: grid; place-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${C.mist};
  transition: background .2s, color .2s;
}
.view-btn.active { background: ${C.forest}; color: ${C.sand}; }

/* ── ACTIVE FILTERS BAR ── */
.active-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.active-filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  background: rgba(194,82,26,.08);
  border: 1px solid rgba(194,82,26,.18);
  font-size: .76rem;
  font-weight: 600;
  color: ${C.terraDk};
}
.active-filter-tag button {
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${C.terra};
  display: flex;
  padding: 0;
  margin-left: 2px;
}
.clear-all {
  font-size: .76rem;
  font-weight: 600;
  color: ${C.mist};
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: color .2s;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.clear-all:hover { color: ${C.terra}; }

/* ── STATS STRIP ── */
.stats-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 28px;
}
@media (max-width: 700px) { .stats-strip { grid-template-columns: repeat(2,1fr); } }
.strip-card {
  background: white;
  border-radius: 16px;
  padding: 16px 18px;
  border: 1px solid rgba(26,61,40,.07);
  box-shadow: 0 2px 12px rgba(26,61,40,.04);
  display: flex;
  align-items: center;
  gap: 12px;
}
.strip-icon {
  width: 36px; height: 36px;
  border-radius: 10px;
  display: grid; place-items: center;
  flex: none;
}
.strip-val {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${C.ink};
  line-height: 1;
}
.strip-lbl { font-size: .74rem; color: ${C.mist}; margin-top: 2px; }

/* ── GRID VIEW ── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
@media (max-width: 1100px) { .cards-grid { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 640px)  { .cards-grid { grid-template-columns: 1fr; } }

.dossier-card {
  background: white;
  border-radius: 20px;
  border: 1.5px solid rgba(26,61,40,.07);
  box-shadow: 0 2px 14px rgba(26,61,40,.04);
  cursor: pointer;
  transition: transform .28s, box-shadow .28s, border-color .28s;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  position: relative;
}
.dossier-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(26,61,40,.1);
  border-color: rgba(194,82,26,.2);
}
.dossier-card.urgent-card {
  border-color: rgba(194,82,26,.25);
}
/* accent top bar */
.card-accent {
  height: 4px;
  background: linear-gradient(90deg, ${C.forestLt}, ${C.terra});
  opacity: .6;
}
.dossier-card.urgent-card .card-accent {
  background: linear-gradient(90deg, ${C.terra}, ${C.goldLt});
  opacity: 1;
}
.card-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.card-ref {
  font-weight: 700;
  font-size: .95rem;
  color: ${C.ink};
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: -.02em;
}
.urgent-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(194,82,26,.12);
  color: ${C.terraDk};
  font-size: .68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  flex: none;
}
.card-location {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: .84rem;
  color: ${C.mist};
  font-weight: 500;
}
.card-meta-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.card-meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: .78rem;
  color: rgba(110,128,112,.7);
}
.card-chips {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: .71rem;
  font-weight: 700;
  white-space: nowrap;
}
.status-chip .s-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  flex: none;
}
.card-footer {
  padding: 13px 20px;
  border-top: 1px solid rgba(26,61,40,.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(26,61,40,.015);
}
.card-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: .76rem;
  color: rgba(110,128,112,.6);
}
.card-arrow {
  color: rgba(110,128,112,.35);
  transition: transform .2s, color .2s;
}
.dossier-card:hover .card-arrow { transform: translateX(3px); color: ${C.terra}; }

/* ── LIST VIEW ── */
.list-view { display: grid; gap: 10px; }
.list-row {
  background: white;
  border-radius: 16px;
  border: 1.5px solid rgba(26,61,40,.07);
  box-shadow: 0 2px 10px rgba(26,61,40,.04);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: transform .25s, box-shadow .25s, border-color .25s;
}
.list-row:hover {
  transform: translateX(4px);
  box-shadow: 0 8px 28px rgba(26,61,40,.09);
  border-color: rgba(194,82,26,.18);
}
.list-avatar {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${C.forest}, ${C.forestLt});
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${C.sand};
  flex: none;
}
.list-ref { font-weight: 700; font-size: .92rem; color: ${C.ink}; }
.list-loc { font-size: .8rem; color: ${C.mist}; margin-top: 2px; }
.list-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}
.list-date { font-size: .76rem; color: rgba(110,128,112,.55); white-space: nowrap; }

/* ── EMPTY ── */
.empty-state {
  text-align: center;
  padding: 72px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.empty-icon-ring {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: rgba(26,61,40,.06);
  display: grid; place-items: center;
  color: ${C.mist};
}
.empty-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${C.ink};
}
.empty-sub { font-size: .88rem; color: ${C.mist}; max-width: 360px; line-height: 1.7; }

/* ── LOADER ── */
.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 14px;
}
.loader-ring {
  width: 36px; height: 36px;
  border: 2.5px solid rgba(26,61,40,.1);
  border-top-color: ${C.terra};
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loader-text { font-size: .85rem; color: ${C.mist}; }
`;

/* ─── CARD ─── */
function DossierCard({ d, onClick }) {
  const st  = STATUS_STYLE[d.statut]  || { bg:'rgba(110,128,112,.08)', color: C.mist, dot: C.mist };
  const res = RESULTAT_STYLE[d.resultat] || RESULTAT_STYLE.EN_ATTENTE;
  const initials = (d.reference || 'TT').replace(/[^A-Z0-9]/gi,'').slice(0,2).toUpperCase();

  return (
    <div className={`dossier-card ${d.urgent ? 'urgent-card' : ''}`} onClick={onClick}>
      <div className="card-accent" />
      <div className="card-body">
        <div className="card-top">
          <div className="card-ref">{d.reference}</div>
          {d.urgent && (
            <span className="urgent-badge">
              <AlertTriangle size={10} /> Urgent
            </span>
          )}
        </div>

        <div className="card-location">
          <MapPin size={13} style={{ color: C.terra, flexShrink:0 }} />
          {d.localisation}
        </div>

        <div className="card-meta-row">
          {d.superficie && (
            <span className="card-meta-item">
              <Ruler size={11} /> {d.superficie} m²
            </span>
          )}
          {d.prix_achat && (
            <span className="card-meta-item">
              <DollarSign size={11} />
              {Number(d.prix_achat).toLocaleString('fr')} {d.devise}
            </span>
          )}
          {d.mandataire_nom && (
            <span className="card-meta-item">
              <User size={11} /> {d.mandataire_nom}
            </span>
          )}
        </div>

        <div className="card-chips">
          <span className="status-chip" style={{ background: st.bg, color: st.color }}>
            <span className="s-dot" style={{ background: st.dot }} />
            {d.statut.replace(/_/g,' ')}
          </span>
          <span className="status-chip" style={{ background: res.bg, color: res.color }}>
            {res.label}
          </span>
        </div>
      </div>

      <div className="card-footer">
        <span className="card-date">
          <Calendar size={11} /> {dayjs(d.created_at).format('DD MMM YYYY')}
        </span>
        <ChevronRight size={15} className="card-arrow" />
      </div>
    </div>
  );
}

/* ─── LIST ROW ─── */
function ListRow({ d, onClick }) {
  const st  = STATUS_STYLE[d.statut]  || { bg:'rgba(110,128,112,.08)', color: C.mist, dot: C.mist };
  const res = RESULTAT_STYLE[d.resultat] || RESULTAT_STYLE.EN_ATTENTE;
  const initials = (d.reference || 'TT').replace(/[^A-Z0-9]/gi,'').slice(0,2).toUpperCase();
  return (
    <div className="list-row" onClick={onClick}>
      <div className="list-avatar">{initials}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div className="list-ref">{d.reference}</div>
        <div className="list-loc"><MapPin size={10} style={{ display:'inline', marginRight:3 }} />{d.localisation}</div>
      </div>
      <div className="list-right">
        <span className="status-chip" style={{ background: st.bg, color: st.color }}>
          <span className="s-dot" style={{ background: st.dot }} />
          {d.statut.replace(/_/g,' ')}
        </span>
        <span className="status-chip" style={{ background: res.bg, color: res.color }}>
          {res.label}
        </span>
        {d.urgent && <span className="urgent-badge"><AlertTriangle size={10} /> Urgent</span>}
        <span className="list-date">{dayjs(d.created_at).format('DD/MM/YY')}</span>
        <ChevronRight size={14} style={{ color:'rgba(110,128,112,.35)' }} />
      </div>
    </div>
  );
}

/* ─── MAIN ─── */
export default function DossierList() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState('');
  const [statut,  setStatut]    = useState('');
  const [sort,    setSort]      = useState('date_desc');
  const [view,    setView]      = useState('grid'); // 'grid' | 'list'
  const searchRef = useRef(null);

  useEffect(() => {
    getDossiers()
      .then(({ data }) => setDossiers(data.results || data))
      .finally(() => setLoading(false));
  }, []);

  /* filter + sort */
  const filtered = dossiers
    .filter(d => {
      const q = search.toLowerCase();
      const matchSearch = !q || [d.reference, d.localisation, d.mandataire_nom].some(v => v?.toLowerCase().includes(q));
      const matchStatut = !statut || d.statut === statut;
      return matchSearch && matchStatut;
    })
    .sort((a, b) => {
      if (sort === 'date_desc') return dayjs(b.created_at).unix() - dayjs(a.created_at).unix();
      if (sort === 'date_asc')  return dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      if (sort === 'ref_asc')   return a.reference.localeCompare(b.reference);
      if (sort === 'urgent')    return (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0);
      return 0;
    });

  /* stats */
  const total   = dossiers.length;
  const enCours = dossiers.filter(d => !['CLOTURE'].includes(d.statut)).length;
  const valides = dossiers.filter(d => d.resultat === 'VALIDE').length;
  const urgents = dossiers.filter(d => d.urgent).length;

  const hasFilters = !!search || !!statut;
  const clearAll = () => { setSearch(''); setStatut(''); };

  if (loading) return (
    <div className="dl-page">
      <style>{css}</style>
      <div className="loader">
        <div className="loader-ring" />
        <span className="loader-text">Chargement des dossiers…</span>
      </div>
    </div>
  );

  return (
    <div className="dl-page">
      <style>{css}</style>

      {/* ── HEADER ── */}
      <motion.div className="dl-header"
        initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }}
        transition={{ duration:.5, ease:[.22,1,.36,1] }}>
        <div>
          <div className="dl-eyebrow">Gestion</div>
          <h1 className="dl-title">
            Mes dossiers
            <span className="dl-count">{filtered.length}</span>
          </h1>
        </div>
        <button className="btn-new" onClick={() => navigate('/dossiers/nouveau')}>
          <Plus size={16} /> Nouveau dossier
        </button>
      </motion.div>

      {/* ── STATS STRIP ── */}
      <motion.div className="stats-strip"
        initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
        transition={{ delay:.08, duration:.5, ease:[.22,1,.36,1] }}>
        {[
          { label:'Total',    val: total,   iconBg:'rgba(26,61,40,.09)',   iconColor: C.forest,   icon: <FolderOpen size={16} /> },
          { label:'En cours', val: enCours, iconBg:'rgba(200,144,42,.1)',  iconColor: C.gold,     icon: <SlidersHorizontal size={16} /> },
          { label:'Validés',  val: valides, iconBg:'rgba(47,102,69,.1)',   iconColor: C.forestLt, icon: <Filter size={16} /> },
          { label:'Urgents',  val: urgents, iconBg:'rgba(194,82,26,.1)',   iconColor: C.terra,    icon: <AlertTriangle size={16} /> },
        ].map(({ label, val, iconBg, iconColor, icon }) => (
          <div key={label} className="strip-card">
            <div className="strip-icon" style={{ background: iconBg, color: iconColor }}>{icon}</div>
            <div>
              <div className="strip-val">{val}</div>
              <div className="strip-lbl">{label}</div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* ── TOOLBAR ── */}
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.14, duration:.5 }}>
        <div className="dl-toolbar">
          {/* search */}
          <div className="search-wrap">
            <Search size={15} className="search-icon" />
            <input ref={searchRef} className="search-input" placeholder="Référence, localisation, mandataire…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <AnimatePresence>
              {search && (
                <motion.button className="search-clear"
                  initial={{ opacity:0, scale:.8 }} animate={{ opacity:1, scale:1 }}
                  exit={{ opacity:0, scale:.8 }} transition={{ duration:.15 }}
                  onClick={() => { setSearch(''); searchRef.current?.focus(); }}>
                  <X size={13} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* statut chips */}
          <div className="filter-chips">
            {STATUTS.slice(0,5).map(({ value, label }) => (
              <button key={value} className={`filter-chip ${statut === value ? 'active' : ''}`}
                onClick={() => setStatut(statut === value ? '' : value)}>
                {value && (
                  <span className="chip-dot" style={{
                    background: statut === value ? C.sand : (STATUS_STYLE[value]?.dot || C.mist)
                  }} />
                )}
                {label}
              </button>
            ))}
          </div>

          {/* sort */}
          <select className="sort-select" value={sort} onChange={e => setSort(e.target.value)}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {/* view toggle */}
          <div className="view-toggle">
            <button className={`view-btn ${view === 'grid' ? 'active' : ''}`} onClick={() => setView('grid')}
              title="Vue grille">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <rect x="0" y="0" width="6" height="6" rx="1.5"/>
                <rect x="8" y="0" width="6" height="6" rx="1.5"/>
                <rect x="0" y="8" width="6" height="6" rx="1.5"/>
                <rect x="8" y="8" width="6" height="6" rx="1.5"/>
              </svg>
            </button>
            <button className={`view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}
              title="Vue liste">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <rect x="0" y="1" width="14" height="2.5" rx="1.2"/>
                <rect x="0" y="5.8" width="14" height="2.5" rx="1.2"/>
                <rect x="0" y="10.5" width="14" height="2.5" rx="1.2"/>
              </svg>
            </button>
          </div>
        </div>

        {/* active filters */}
        <AnimatePresence>
          {hasFilters && (
            <motion.div className="active-filters"
              initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
              exit={{ opacity:0, height:0 }} transition={{ duration:.25 }}>
              <Filter size={12} style={{ color: C.mist }} />
              {search && (
                <span className="active-filter-tag">
                  "{search}"
                  <button onClick={() => setSearch('')}><X size={10} /></button>
                </span>
              )}
              {statut && (
                <span className="active-filter-tag">
                  {STATUTS.find(s => s.value === statut)?.label}
                  <button onClick={() => setStatut('')}><X size={10} /></button>
                </span>
              )}
              <button className="clear-all" onClick={clearAll}>Tout effacer</button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── CONTENT ── */}
      <AnimatePresence mode="wait">
        {filtered.length === 0 ? (
          <motion.div key="empty"
            initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            exit={{ opacity:0 }} transition={{ duration:.4 }}>
            <div className="empty-state">
              <div className="empty-icon-ring"><FolderOpen size={28} /></div>
              <div className="empty-title">Aucun dossier trouvé</div>
              <p className="empty-sub">
                {hasFilters
                  ? "Aucun dossier ne correspond à vos filtres. Essayez d'élargir votre recherche."
                  : "Vous n'avez pas encore de dossiers. Commencez par en créer un."}
              </p>
              {!hasFilters && (
                <button className="btn-new" style={{ marginTop:8 }} onClick={() => navigate('/dossiers/nouveau')}>
                  <Plus size={15} /> Nouveau dossier
                </button>
              )}
              {hasFilters && (
                <button className="btn-new" style={{ marginTop:8, background: C.mist }} onClick={clearAll}>
                  Effacer les filtres
                </button>
              )}
            </div>
          </motion.div>
        ) : view === 'grid' ? (
          <motion.div key="grid" className="cards-grid"
            initial={{ opacity:0 }} animate={{ opacity:1 }}
            exit={{ opacity:0 }} transition={{ duration:.3 }}>
            {filtered.map((d, i) => (
              <motion.div key={d.id}
                initial={{ opacity:0, y:18 }}
                animate={{ opacity:1, y:0 }}
                transition={{ delay: i * .04, duration:.45, ease:[.22,1,.36,1] }}>
                <DossierCard d={d} onClick={() => navigate(`/dossiers/${d.id}`)} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div key="list" className="list-view"
            initial={{ opacity:0 }} animate={{ opacity:1 }}
            exit={{ opacity:0 }} transition={{ duration:.3 }}>
            {filtered.map((d, i) => (
              <motion.div key={d.id}
                initial={{ opacity:0, x:-14 }}
                animate={{ opacity:1, x:0 }}
                transition={{ delay: i * .03, duration:.4, ease:[.22,1,.36,1] }}>
                <ListRow d={d} onClick={() => navigate(`/dossiers/${d.id}`)} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}